<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { watch } from 'vue';
import {
  CustomMetadataService,
  type EntityCustomMetadataDto,
  type EntityCustomMetadataEntryUserDto,
  type GetCustomMetadataListResponseDto,
  type GetCustomMetadataResponseDto,
  type GetCustomMetadataEntryUserValueResponseDto,
} from '@/api';
import SAvatars from '@/components/SAvatars/SAvatars.vue';

interface CustomMetadataUserSelectorProps {
  metadata: GetCustomMetadataListResponseDto;
  entityCustomMetadata?: EntityCustomMetadataDto;
}
const props = defineProps({
  metadata: null,
  entityCustomMetadata: null
});

type CustomMetadataLabelSelectorEmits = {
  (event: 'update', metadataId: string, entries: EntityCustomMetadataEntryUserDto[]): void,
}
const emits = defineEmits(["update"]);

let loading = _ref<boolean>(false);
// Local v-model proxy
let selectedEntries = _ref<string[] | string | null>([]);
let fetchedMetadata = _ref<GetCustomMetadataResponseDto>();
let searchString = _ref<string>('');

const allowMultiple = _computed(() => {
  if (fetchedMetadata.value) {
    return fetchedMetadata.value.isAllowMultipleValues;
  }
  return false;
});

const selectorItems = _computed(() => {
  if (fetchedMetadata.value) {
    return fetchedMetadata.value.entries.data as GetCustomMetadataEntryUserValueResponseDto[];
  }
  return [];
});

/**
 * Get details on the custom metadata
 * @param metadataId
 */
const fetchMetadataInfos = (metadataId: string) => {
  loading.value = true;
  CustomMetadataService.getCustomMetadata({ id: metadataId, itemsPerPage: -1 })
      .then((response) => {
        fetchedMetadata.value = response;
      })
      .finally(() => {
        loading.value = false;
      });
};

/**
 * Handle selector update
 * Re-map selected entries so we also get type, ID and value from the fetched metadata on emitted change
 * @param newSelectedEntries
 */
const onSelectorUpdate = (newSelectedEntries: string | string[]) => {
  const entries = Array.isArray(newSelectedEntries) ? newSelectedEntries : [newSelectedEntries];
  const selectedEntriesWithValues: EntityCustomMetadataEntryUserDto[] = entries
    .map((entryId) => selectorItems.value.find((item) => item.id === entryId) as EntityCustomMetadataEntryUserDto | undefined)
    .filter((entry): entry is EntityCustomMetadataEntryUserDto => entry !== undefined);
  emits('update', props.metadata.id, selectedEntriesWithValues);
  searchString.value = '';
};

watch(() => props.metadata, (newMetadata) => {
  if (newMetadata) {
    fetchMetadataInfos(newMetadata.id);
  }
}, { immediate: true });

// Pre-set selected entries based on the entity custom metadata
watch([() => props.entityCustomMetadata, () => allowMultiple.value], ([newEntityCustomMetadata, newAllowMultiple]) => {
  if (newEntityCustomMetadata) {
    if (newAllowMultiple) {
      selectedEntries.value = newEntityCustomMetadata.entries.map((entry) => entry.id);
    } else {
      // Metadata cannot be multiple: only take first entry
      const [firstEntry] = newEntityCustomMetadata.entries as GetCustomMetadataEntryUserValueResponseDto[];
      selectedEntries.value = firstEntry?.id || null;
    }
  }
}, { immediate: true });

</script>

<template lang="pug">
.custom-metadata-user-selector
    v-autocomplete.mt-1(
      :disabled="!fetchedMetadata || loading"
      clearable
      item-text="name"
      item-value="id"
      :items="selectorItems"
      :placeholder="$t('custom_metadata.selectors.user_placeholder', { name: props.metadata.name.toLowerCase() })"
      :menu-props="{ bottom: true, offsetY: true }"
      :search-input.sync="searchString"
      @blur="searchString=''"
      dense outlined
      :multiple="allowMultiple"
      hide-details
      data-track="custom-metadata-user-selector"
      v-model="selectedEntries"
      @change="onSelectorUpdate"
    )
      template(v-slot:item="{ item, attrs, on }")
        v-simple-checkbox( v-if="allowMultiple" :value="attrs.inputValue" v-on="on")
        SAvatars(
          :users="[item]"
          data-track="custom-metadata-user-selector-item"
        )

      template(v-slot:selection="{ item }")
        SAvatars.mr-2(
          :users="[item]"
          data-track="custom-metadata-user-selector-item-selected"
        )

      template(v-slot:no-data)
        .pa-2.text-center No options available:

</template>
